import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const location = useLocation();

  // Redirect if already authenticated
  if (isAuthenticated()) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      const from = location.state?.from?.pathname || '/';
      window.location.href = from;
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ background: 'var(--vault-bg)' }}>
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5"
          style={{
            backgroundImage: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 50px,
              var(--vault-border) 50px,
              var(--vault-border) 51px
            ), repeating-linear-gradient(
              90deg,
              transparent,
              transparent 50px,
              var(--vault-border) 50px,
              var(--vault-border) 51px
            )`
          }}
        />
      </div>

      <div className="relative max-w-md w-full">
        {/* Top classification banner */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded bg-[var(--vault-elevated)] border border-[var(--vault-border)]">
            <ShieldCheckIcon className="w-4 h-4 text-[var(--spotlight-gold)]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)]">
              Secure Access Portal
            </span>
          </div>
        </div>

        {/* Main card */}
        <div className="vault-card-elevated p-8">
          {/* Logo section */}
          <div className="text-center mb-8">
            <div className="inline-block mb-6">
              <img
                src="/DECLASS GIF.gif"
                alt="DECLASS"
                className="h-48 w-auto drop-shadow-2xl"
              />
            </div>
            <h1 className="text-2xl font-bold tracking-wider text-[var(--text-primary)]">
              DECLASS
            </h1>
            <p className="mt-2 text-sm font-mono text-[var(--text-muted)]">
              Document Classification & Intelligence Processing
            </p>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="vault-input w-full"
                  placeholder="Enter username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  autoComplete="username"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="vault-input w-full"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 rounded bg-[rgba(160,64,64,0.15)] border border-[var(--error)]">
                <p className="text-sm text-[var(--error)] font-mono">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="vault-btn vault-btn-primary w-full justify-center"
            >
              <LockClosedIcon className="w-4 h-4" />
              {loading ? 'Authenticating...' : 'Access System'}
            </button>
          </form>
        </div>

        {/* Bottom security notice */}
        <div className="mt-6 text-center">
          <p className="text-[10px] font-mono text-[var(--text-muted)] uppercase tracking-wider">
            Authorized Personnel Only
          </p>
          <p className="mt-1 text-[10px] font-mono text-[var(--text-muted)]">
            All access is monitored and logged
          </p>
        </div>

        {/* Version info */}
        <div className="mt-8 text-center">
          <span className="text-[10px] font-mono text-[var(--vault-border)]">
            Cognitive Fabric Platform v1.0
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
